import React from 'react';
import { useNavigate } from 'react-router-dom';
import useAdminNotificationTestForms from '../hooks/admin/useAdminNotificationTestForms';
import useRestrictedAccessAdminSettings from '../hooks/admin/useRestrictedAccessAdminSettings';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardTitle,
  Form,
  FormField,
  GridCol,
  GridRow,
  Page,
  PageHeader,
  Switch,
  Text,
  Textarea,
  TextInput,
} from '../components/ui';

const AdminNotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    emailTo,
    setEmailTo,
    emailSubject,
    setEmailSubject,
    emailText,
    setEmailText,
    sendingEmail,
    inAppSubject,
    setInAppSubject,
    inAppText,
    setInAppText,
    sendingInApp,
    status,
    submitEmailTest,
    submitInAppTest,
  } = useAdminNotificationTestForms();
  const {
    enabled: restrictedAccessEnabled,
    loading: restrictedAccessLoading,
    saving: restrictedAccessSaving,
    status: restrictedAccessStatus,
    handleEnabledChange,
    saveSettings,
  } = useRestrictedAccessAdminSettings();

  const handleBack = () => navigate(-1);

  return (
    <Page marginTop={4} paddingBottom={5}>
      <PageHeader
        title="Admin - Test notifications"
        actions={(
          <Button type="button" variant="secondary" onClick={handleBack}>
            Retour
          </Button>
        )}
      />

      <Text tone="muted">
        Cette page permet de verifier le canal e-mail en conditions reelles ou en dry-run.
      </Text>

      {status && (
        <Alert variant={status.isError ? 'danger' : 'success'}>
          {status.message}
        </Alert>
      )}

      <GridRow gap={4}>
        <GridCol span={12} lg={8}>
          <Card shadow="sm" fullHeight>
            <CardBody>
              <CardTitle>Test e-mail</CardTitle>
              <Form onSubmit={submitEmailTest}>
                <FormField label="Destinataire e-mail" htmlFor="admin-email-to">
                  <TextInput
                    id="admin-email-to"
                    type="email"
                    value={emailTo}
                    onChange={(event) => setEmailTo(event.target.value)}
                    required
                  />
                </FormField>
                <FormField label="Sujet" htmlFor="admin-email-subject">
                  <TextInput
                    id="admin-email-subject"
                    type="text"
                    value={emailSubject}
                    onChange={(event) => setEmailSubject(event.target.value)}
                    required
                  />
                </FormField>
                <FormField label="Message e-mail" htmlFor="admin-email-text">
                  <Textarea
                    id="admin-email-text"
                    rows={4}
                    value={emailText}
                    onChange={(event) => setEmailText(event.target.value)}
                    required
                  />
                </FormField>
                <Button type="submit" variant="primary" disabled={sendingEmail}>
                  {sendingEmail ? 'Envoi en cours...' : 'Envoyer un test e-mail'}
                </Button>
              </Form>
            </CardBody>
          </Card>
        </GridCol>
        <GridCol span={12} lg={8}>
          <Card shadow="sm" fullHeight>
            <CardBody>
              <CardTitle>Lien de connexion par e-mail</CardTitle>
              <Text tone="muted" marginBottom={3}>
                Active ou desactive le formulaire affiche sur la page d acces restreint.
              </Text>

              {restrictedAccessStatus && (
                <Alert variant={restrictedAccessStatus.isError ? 'danger' : 'success'}>
                  {restrictedAccessStatus.message}
                </Alert>
              )}

              <Switch
                id="restricted-access-enabled"
                label={'Proposer "Recevoir mon lien de connexion"'}
                marginBottom={3}
                checked={restrictedAccessEnabled}
                onChange={handleEnabledChange}
                disabled={restrictedAccessLoading || restrictedAccessSaving}
              />

              <Button
                type="button"
                variant="primary"
                outline
                onClick={() => { void saveSettings(); }}
                disabled={restrictedAccessLoading || restrictedAccessSaving}
              >
                {restrictedAccessSaving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </CardBody>
          </Card>
        </GridCol>
        <GridCol span={12} lg={8}>
          <Card shadow="sm" fullHeight>
            <CardBody>
              <CardTitle>Test notification in-app</CardTitle>
              <Form onSubmit={submitInAppTest}>
                <FormField label="Titre" htmlFor="admin-inapp-subject">
                  <TextInput
                    id="admin-inapp-subject"
                    type="text"
                    value={inAppSubject}
                    onChange={(event) => setInAppSubject(event.target.value)}
                    required
                  />
                </FormField>
                <FormField label="Message in-app" htmlFor="admin-inapp-text">
                  <Textarea
                    id="admin-inapp-text"
                    rows={4}
                    value={inAppText}
                    onChange={(event) => setInAppText(event.target.value)}
                    required
                  />
                </FormField>
                <Button type="submit" variant="primary" outline disabled={sendingInApp}>
                  {sendingInApp ? 'Envoi en cours...' : 'Envoyer une notification sur mon compte admin'}
                </Button>
              </Form>
            </CardBody>
          </Card>
        </GridCol>
      </GridRow>
    </Page>
  );
};

export default AdminNotificationsPage;
